import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { Auth } from '@angular/fire/auth';
import { environment } from '../../environments/environment';

export interface DuplicateCheckRequest {
  panNumber?: string;
  gstNumber?: string;
  corporateIdentificationNumber?: string;
  businessRegistrationNumber?: string;
}

export interface DuplicateCheckResponse {
  isDuplicate: boolean;
  conflictFields: string[];
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private readonly baseUrl = `${environment.apiUrl}`;

  constructor(
    private http: HttpClient,
    private auth: Auth
  ) {}

  /**
   * Get HTTP headers with authorization token
   */
  private async getHeaders(): Promise<HttpHeaders> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    if (this.auth.currentUser) {
      try {
        const token = await this.auth.currentUser.getIdToken();
        headers = headers.set('Authorization', `Bearer ${token}`);
      } catch (error) {
        console.warn('⚠️ Failed to get ID token:', error);
        throw new Error('Authentication failed. Please sign in again.');
      }
    }
    
    return headers;
  }

  /**
   * Check for duplicate organization registration
   */
  checkDuplicateRegistration(request: DuplicateCheckRequest): Observable<DuplicateCheckResponse> {
    console.log('🔍 Checking for duplicate organization registration:', request);
    
    return from(this.getHeaders()).pipe(
      switchMap(headers => 
        this.http.post<DuplicateCheckResponse>(`${this.baseUrl}/organizations/check-duplicate`, request, { headers })
          .pipe(
            map(response => {
              console.log('✅ Duplicate check response:', response);
              return response;
            }),
            catchError(error => {
              console.error('❌ Duplicate check failed:', error);
              // Return no duplicates on error to allow registration to proceed
              return [{ isDuplicate: false, conflictFields: [] } as DuplicateCheckResponse];
            })
          )
      )
    );
  }

  /**
   * Create agency registration
   */
  createAgency(agencyData: any): Observable<any> {
    console.log('🏢 Creating agency:', agencyData);
    
    return from(this.getHeaders()).pipe(
      switchMap(headers => 
        this.http.post(`${this.baseUrl}/agencies`, agencyData, { headers })
          .pipe(
            map(response => {
              console.log('✅ Agency created:', response);
              return response;
            }),
            catchError(error => {
              console.error('❌ Agency creation failed:', error);
              throw error;
            })
          )
      )
    );
  }

  /**
   * Create corporate registration
   */
  createCorporate(corporateData: any): Observable<any> {
    console.log('🏢 Creating corporate:', corporateData);
    
    return from(this.getHeaders()).pipe(
      switchMap(headers => 
        this.http.post(`${this.baseUrl}/corporates`, corporateData, { headers })
          .pipe(
            map(response => {
              console.log('✅ Corporate created:', response);
              return response;
            }),
            catchError(error => {
              console.error('❌ Corporate creation failed:', error);
              throw error;
            })
          )
      )
    );
  }

  /**
   * Validate PAN number format
   */
  validatePAN(pan: string): { isValid: boolean; error?: string } {
    if (!pan || pan.trim().length === 0) {
      return { isValid: true }; // Optional field
    }
    
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(pan)) {
      return { isValid: false, error: 'Please enter a valid PAN number (e.g., ABCDE1234F)' };
    }
    
    return { isValid: true };
  }

  /**
   * Validate GST number format
   */
  validateGST(gst: string): { isValid: boolean; error?: string } {
    if (!gst || gst.trim().length === 0) {
      return { isValid: true }; // Optional field
    }
    
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstRegex.test(gst)) {
      return { isValid: false, error: 'Please enter a valid GST number' };
    }
    
    return { isValid: true };
  }

  /**
   * Validate Corporate Identification Number (CIN)
   */
  validateCIN(cin: string): { isValid: boolean; error?: string } {
    if (!cin || cin.trim().length === 0) {
      return { isValid: false, error: 'Corporate Identification Number is required for companies' };
    }
    
    // Basic CIN format validation - can be enhanced
    if (cin.length < 15 || cin.length > 21) {
      return { isValid: false, error: 'Please enter a valid Corporate Identification Number' };
    }
    
    return { isValid: true };
  }
}
